import { useState, useEffect } from "react";
import {
  Button,
  Spin,
  Row,
  Col,
  InputNumber,
  message,
  Modal,
  Select,
  Input,
  Table,
  Grid,
} from "antd";
import api from "../../services/api";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";

export default function Cart() {
  const navigate = useNavigate();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [deliveryType, setDeliveryType] = useState("Normal");
  const [notes, setNotes] = useState("");
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const {
    items,
    loading,
    clearCart,
    removeItem: removeFromContext,
    updateQty,
    loadCart,
  } = useCart();

  useEffect(() => {
    loadCart();
  }, []);

  /* ✅ REMOVE ITEM */
  const removeItem = async (item) => {
    try {
      if (item.cartId) {
        await api.delete(`/cart/${item.cartId}`);
      }

      removeFromContext(item.cartId);

      message.success("Item removed");
    } catch (err) {
      console.error(err);
      message.error("Failed to remove item");
    }
  };

  /* ✅ UPDATE QTY */
  const updateItemQty = async (item, qty) => {
    const safeQty = Number(qty);

    if (!safeQty || safeQty < 1) return;

    try {
      // update backend first
      if (item.cartId) {
        await api.put(`/cart/${item.cartId}`, {
          quantity: safeQty,
        });
      }

      // then update local state
      await loadCart();
    } catch (err) {
      console.error(err);

      message.error("Update failed");
    }
  };

  /* ✅ TOTAL */
  const total = items.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.qty || 1),
    0,
  );

  if (loading) return <Spin />;

  const groupedItems = items.reduce((acc, item) => {
    const subcategoryName = item.product?.subcategory?.name || "General";

    if (!acc[subcategoryName]) {
      acc[subcategoryName] = [];
    }

    acc[subcategoryName].push(item);

    return acc;
  }, {});

  /* ✅ SORT SUBCATEGORY GROUPS */
  const sortedGroupedItems = Object.entries(groupedItems).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  const desktopColumns = [
    {
      title: "Image",
      width: 100,
      render: (_, item) => {
        const image = item.image
          ? `${process.env.REACT_APP_API_URL}${item.image}`
          : "/placeholder.png";

        return (
          <img
            src={image}
            alt=""
            style={{
              width: 60,
              height: 60,
              objectFit: "contain",
            }}
          />
        );
      },
    },
    {
      title: "Name",
      render: (_, item) => <strong>{item.name}</strong>,
    },
    {
      title: "Color",
      width: 120,
      render: (_, item) => {
        const color = item.attributes?.find(
          (a) => a.Attribute?.name?.toLowerCase() === "colors",
        )?.AttributeValue?.value;

        return color || "-";
      },
    },
    {
      title: "Quantity",
      width: 140,
      render: (_, item) => (
        <InputNumber
          min={1}
          value={item.qty}
          onChange={(val) => updateItemQty(item, val)}
        />
      ),
    },
    {
      title: "Price",
      width: 120,
      render: (_, item) => `£ ${Number(item.price || 0).toFixed(2)}`,
    },
    {
      title: "Total",
      width: 120,
      render: (_, item) =>
        `£ ${(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}`,
    },
    {
      title: "",
      width: 100,
      render: (_, item) => (
        <Button danger size="small" onClick={() => removeItem(item)}>
          Remove
        </Button>
      ),
    },
  ];

  const mobileColumns = [
    {
      title: "",
      render: (_, item) => {
        const color =
          item.attributes?.find(
            (a) => a.Attribute?.name?.toLowerCase() === "colors",
          )?.AttributeValue?.value || "-";

        return (
          <div>
            {/* Row 1 */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div>
                <strong>{item.name}</strong>
              </div>

              <div>
                <strong>Variant:</strong>
                {color}
              </div>
            </div>

            {/* Row 2 */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <InputNumber
                min={1}
                value={item.qty}
                size="small"
                onChange={(val) => updateItemQty(item, val)}
              />

              {/* <span>£ {Number(item.price || 0).toFixed(2)}</span> */}

              <strong>
                £ {(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}
              </strong>

              <Button danger size="small" onClick={() => removeItem(item)}>
                Remove
              </Button>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <h2>Your Cart</h2>

      {items.length === 0 && <p>Cart is empty</p>}

      {sortedGroupedItems.map(([subcategoryName, subItems]) => (
        <div key={subcategoryName} style={{ marginBottom: 30 }}>
          <div
            style={{
              background: "#f5f5f5",
              padding: "10px 15px",
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 6,
              marginBottom: 10,
            }}
          >
            {subcategoryName}
          </div>

          <Table
            rowKey="cartId"
            pagination={false}
            columns={isMobile ? mobileColumns : desktopColumns}
            dataSource={[...subItems].sort((a, b) =>
              (a.model_name || "").localeCompare(b.model_name || ""),
            )}
            bordered={!isMobile}
          />
        </div>
      ))}
      {items.length > 0 && (
        <>
          <Row justify="end" style={{ marginTop: 20 }}>
            <Col>
              <h3>Total: £ {total.toFixed(2)}</h3>
            </Col>
          </Row>

          <Button
            type="primary"
            size="large"
            onClick={() => setCheckoutOpen(true)}
          >
            Checkout
          </Button>
        </>
      )}
      {/* ✅ CHECKOUT MODAL */}
      <Modal
        title="Delivery Options"
        open={checkoutOpen}
        onCancel={() => setCheckoutOpen(false)}
        onOk={async () => {
          try {
            const res = await api.post("/orders/checkout", {
              deliveryType,
              notes,
            });

            clearCart();

            setCheckoutOpen(false);

            navigate(`/order-success?order=${res.data.orderNumber}`);
          } catch (err) {
            message.error(err.response?.data?.message || "Checkout failed");
          }
        }}
      >
        <div style={{ marginBottom: 15 }}>
          <label>Delivery Option</label>

          <Select
            style={{ width: "100%", marginTop: 5 }}
            value={deliveryType}
            onChange={setDeliveryType}
            options={[
              { label: "Normal", value: "Normal" },
              {
                label: "Premium Delivery by 12pm",
                value: "Premium Delivery by 12pm",
              },
              {
                label: "Special Saturday Delivery",
                value: "Special Saturday Delivery",
              },
              { label: "Collection", value: "Collection" },
            ]}
          />
        </div>

        <div>
          <label>Notes</label>

          <Input.TextArea
            rows={4}
            placeholder="Enter delivery notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
